<template>
  <v-select
    ref="select"
    v-model="input"
    class="h-auto w-auto py-0"
    :class="{
      'is-invalid': state === false,
      'is-valid': state === true,
    }"
    v-bind="$attrs"
    :options="suggestions"
    :placeholder="placeholder"
    :reduce="reduceOption"
    :close-on-select="false"
    :map-keydown="mapKeyDown"
    :filterable="isFilterable"
    multiple
    @search="runLookup"
  >
    <template #search="{ attributes, events }">
      <input class="vs__search" v-bind="attributes" v-on="{ ...events, input: onInput, blur: onBlur }" />
    </template>

    <template #option="option">
      <user-media v-if="!!option.username" :user="option" minimal :avatar-size="35"></user-media>
    </template>

    <template #selected-option-container="{ option, deselect }">
      <badge
        :variant="badgeVariant(getSelectedOptionLabel(option))"
        class="mr-1"
        with-remove
        @remove="deselect(option[optionSelectableField])"
      >
        {{ getSelectedOptionLabel(option) }}
      </badge>
    </template>
    <template #open-indicator="{ attributes }">
      <i class="fa fa-caret-square-down" v-bind="attributes"></i>
    </template>
  </v-select>
</template>

<script>
  import vSelect from 'vue-select';
  import UserMedia from '@/Molecules/UserMedia';
  import axios from 'axios';
  import Badge from './Badge';
  import { formValidationMixin } from '../validators';

  let selectedOptions = {};

  export default {
    name: 'FormTagInput',
    mixins: [formValidationMixin],

    components: {
      Badge,
      vSelect,
      UserMedia,
    },

    props: {
      value: Array,
      options: Array,
      placeholder: String,
      rules: Array,
      validation: Object,
      optionSelectableField: {
        type: String,
        default: 'label',
      },
      isFilterable: {
        type: Boolean,
        default: true,
      },
      isSearchable: {
        type: Boolean,
        default: true,
      },
      variant: String,
    },

    data() {
      return {
        suggestions: this.options,
        cancelSource: null,
        selectedOptions: { ...selectedOptions },
      };
    },

    computed: {
      input: {
        get() {
          return this.value || [];
        },

        set(value) {
          this.$emit('input', value);
        },
      },

      state() {
        return this.inputState(this.validation);
      },
    },

    methods: {
      validateTag(value) {
        if (!this.rules || this.rules.length === 0) {
          return true;
        }

        return this.rules.map((rule) => rule.test(value)).reduce((carry, value) => carry || value, false);
      },

      badgeVariant(value) {
        const type = this.variant || 'dark';
        return this.validateTag(value) ? type : 'danger';
      },

      reduceOption(option) {
        if (option[this.optionSelectableField] && !selectedOptions[option[this.optionSelectableField]]) {
          selectedOptions = {
            ...selectedOptions,
            [option[this.optionSelectableField]]: { ...option },
          };

          this.selectedOptions = { ...selectedOptions };
        }

        return option[this.optionSelectableField] || option;
      },

      onInput(event) {
        const value = event.target.value;
        const items = value.split(/[\s,;\t]+/);
        if (items.length > 1) {
          //this ensures a space is added after the last character
          const input = this.input || [];
          this.input = [...input, ...items.filter((item) => item.length).filter((item) => !input.includes(item))];
          this.$refs.select.search = this.validateTag(value) ? value : '';
          return;
        }

        if (value.indexOf('@') === 0) {
          //is a mention and needs this to runLookup
          this.$refs.select.search = this.validateTag(value) ? value : '';
        }
      },

      onBlur(event) {
        const value = event.target.value;

        if (value.length > 0) {
          const input = this.input || [];

          this.input = [...input, value];
        }

        this.$refs.select.search = '';
        this.$refs.select.onSearchBlur();
      },

      onEnter() {
        const value = this.$refs.select.searchEl.value;

        if (value.length > 0) {
          if (!this.input) {
            this.input = [];
          }

          this.input.push(value);
        }

        this.$refs.select.search = '';
      },

      mapKeyDown(map) {
        const preventAndSelect = map[13] || (() => {});

        map[13] = (event) => {
          this.onEnter(event);
          preventAndSelect(event);
        };

        return map;
      },

      runLookup(tag) {
        if (!this.isSearchable) return;

        this.suggestions = [];

        if (tag.indexOf('@') === 0) {
          return this.runLookupMention(tag.substring(1));
        } else if (tag.indexOf('@') > 0) {
          return this.runLookupMention(tag);
        }
      },

      runLookupMention(query) {
        this.cancelSource && this.cancelSource.cancel();
        this.cancelSource = axios.CancelToken.source();

        this.$api('suggestions.mentions', { version: '1.0', term: query }, {}, this.cancelSource.token)
          .then((response) => {
            this.suggestions = response.data.data.map((user) => ({ ...user, label: '@' + user.username }));
          })
          .catch(() => {});
      },

      getSelectedOptionLabel(option) {
        if (selectedOptions[option.label]) {
          return selectedOptions[option.label].label;
        }

        return option.label || option.email || option;
      },
    },
  };
</script>
